import "@/App.css";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Customers from "@/pages/Customers";
import { Agents, Services, PaymentTypes, Branches } from "@/pages/ResourcePages";
import Receipts from "@/pages/Receipts";
import MISReport from "@/pages/MISReport";
import Reports from "@/pages/Reports";
import ReceiptView from "@/pages/ReceiptView";
import DailyStatus from "@/pages/DailyStatus";
import { DailyAssignedService, DailyLoginService } from "@/pages/AssignedService";
import Company from "@/pages/Company";
import Users from "@/pages/Users";
import RolePermissions from "@/pages/RolePermissions";
import LoginHours from "@/pages/LoginHours";
import ServiceReport from "@/pages/ServiceReport";
import StaffReport from "@/pages/StaffReport";
import BankTransactions from "@/pages/BankTransactions";
import Attendance from "@/pages/Attendance";
import Notice from "@/pages/Notice";
import PublicSite from "@/pages/PublicSite";
import Inquiries from "@/pages/Inquiries";
import FollowUp from "@/pages/FollowUp";
import WorkSummary from "@/pages/WorkSummary";
import Messages from "@/pages/Messages";
import SiteAdmin from "@/pages/SiteAdmin";
import ReceiptSettings from "@/pages/ReceiptSettings";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AdminOnly({ children }) {
  const { user } = useAuth();
  if (user?.role !== "admin") return <Navigate to="/" replace />;
  return children;
}

function RequireView({ module, children }) {
  const { user } = useAuth();
  if (!user) return null;
  if (user.role === "admin" || user.role === "branch") return children;
  const p = user.permissions?.[module];
  if (p && p.view) return children;
  return (
    <div className="max-w-md mx-auto mt-24 bg-white border border-slate-200 rounded-md p-8 text-center" data-testid="no-permission">
      <div className="font-display font-black text-2xl text-slate-800 mb-1">No access</div>
      <p className="text-sm text-slate-500">
        You don't have permission to view <span className="font-semibold">{module}</span>. Contact your administrator.
      </p>
    </div>
  );
}

function ManagerOrAdmin({ children }) {
  const { user } = useAuth();
  if (!["admin", "manager", "branch"].includes(user?.role)) return <Navigate to="/" replace />;
  return children;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/" element={<PublicSite />} />
          <Route path="/login" element={<Login />} />
          <Route path="/receipt/:id" element={<ReceiptView />} />
          <Route
            path="/app"
            element={
              <Protected>
                <Layout />
              </Protected>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="inquiries" element={<ManagerOrAdmin><Inquiries /></ManagerOrAdmin>} />
            <Route path="follow-up" element={<RequireView module="Receipt"><FollowUp /></RequireView>} />
            <Route path="customers" element={<RequireView module="Customer"><Customers /></RequireView>} />
            <Route path="agents" element={<RequireView module="Agent"><Agents /></RequireView>} />
            <Route path="services" element={<RequireView module="Service"><Services /></RequireView>} />
            <Route path="receipts" element={<RequireView module="Receipt"><Receipts /></RequireView>} />
            <Route path="daily-assigned-service" element={<RequireView module="Daily Assigned Service"><DailyAssignedService /></RequireView>} />
            <Route path="daily-login-service" element={<RequireView module="Daily Login Service"><DailyLoginService /></RequireView>} />
            <Route path="daily-status" element={<RequireView module="Daily Status"><DailyStatus /></RequireView>} />
            <Route path="mis-report" element={<RequireView module="MIS Report"><MISReport /></RequireView>} />
            <Route path="reports" element={<RequireView module="MIS Report"><Reports /></RequireView>} />
            <Route path="login-hours" element={<ManagerOrAdmin><LoginHours /></ManagerOrAdmin>} />
            <Route path="service-report" element={<RequireView module="Service Report"><ServiceReport /></RequireView>} />
            <Route path="staff-report" element={<RequireView module="Staff Report"><StaffReport /></RequireView>} />
            <Route path="bank-transactions" element={<RequireView module="Bank Transactions"><BankTransactions /></RequireView>} />
            <Route path="attendance" element={<RequireView module="Attendance"><Attendance /></RequireView>} />
            <Route path="work-summary" element={<RequireView module="Work Summary"><WorkSummary /></RequireView>} />
            <Route path="messages" element={<Messages />} />
            <Route path="site-admin" element={<AdminOnly><SiteAdmin /></AdminOnly>} />
            <Route path="notice" element={<AdminOnly><Notice /></AdminOnly>} />
            <Route path="payment-types" element={<RequireView module="Payment Types"><PaymentTypes /></RequireView>} />
            <Route path="branches" element={<AdminOnly><Branches /></AdminOnly>} />
            <Route path="company" element={<AdminOnly><Company /></AdminOnly>} />
            <Route path="receipt-settings" element={<AdminOnly><ReceiptSettings /></AdminOnly>} />
            <Route path="users" element={<AdminOnly><Users /></AdminOnly>} />
            <Route path="role-permissions" element={<AdminOnly><RolePermissions /></AdminOnly>} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
